import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, Ship, Clock, MapPin, ArrowRight, ChevronRight } from "lucide-react";
import { FREIGHT_ROUTES } from "@/data/freightRoutes";
import FreightQuoteForm from "@/components/FreightQuoteForm";

const useDocTitle = (title, description) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta && description) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta?.getAttribute("content");
    if (meta && description) meta.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (created && meta?.parentNode) meta.parentNode.removeChild(meta);
      else if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, [title, description]);
};

const FreightRoutePage = () => {
  const { slug } = useParams();
  const cfg = FREIGHT_ROUTES[slug];
  useDocTitle(cfg?.hero.metaTitle, cfg?.hero.metaDescription);
  if (!cfg) return <Navigate to="/freight/quote" replace />;
  const h = cfg.hero;
  return (
    <div className="bg-[var(--tt-cream)] min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--tt-line)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--tt-navy)] via-[var(--tt-navy-2)] to-[var(--tt-green)] opacity-95" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="relative mx-auto max-w-7xl px-6 py-16 md:py-24">
          <nav className="text-xs text-white/70 mb-4 flex items-center gap-1.5" data-testid="freight-route-breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white">{h.eyebrow}</span>
          </nav>
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs font-semibold text-white uppercase tracking-widest mb-4">
                <MapPin className="h-3.5 w-3.5" /> {h.eyebrow}
              </div>
              <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="freight-route-title">
                {h.title}
              </h1>
              <p className="text-white/85 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
                {h.subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#quote" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tt-navy)] font-bold px-6 py-3 text-sm shadow-lg hover:bg-[var(--tt-accent-2)] transition" data-testid="freight-route-quote-cta">
                  Get a rate <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border-2 border-white/60 text-white font-semibold px-6 py-3 text-sm hover:bg-white/10 transition">
                  Talk to sales
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {cfg.lanes.map((l) => (
                  <div key={l.size} className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-3 text-white">
                    <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> {l.eta}</div>
                    <div className="font-bold mt-1 text-sm">{l.size}</div>
                    <div className="text-[11px] text-white/70 mt-0.5">{l.note}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block">
              <div id="quote-lg" className="rounded-3xl bg-white/95 backdrop-blur p-2">
                <FreightQuoteForm
                  defaultMode={cfg.defaults.mode}
                  defaultOrigin={cfg.defaults.origin}
                  defaultDestination={cfg.defaults.destination}
                  defaultOriginPort={cfg.defaults.originPort}
                  defaultDestinationPort={cfg.defaults.destinationPort}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services strip */}
      <section className="mx-auto max-w-7xl px-6 py-14">
        <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">What's included</div>
        <h2 className="font-display font-black text-[var(--tt-navy)] text-3xl md:text-4xl tracking-tight mt-1">
          End-to-end freight, one team.
        </h2>
        <div className="grid md:grid-cols-2 gap-3 mt-8" data-testid="freight-route-services">
          {cfg.services.map((s) => (
            <div key={s} className="flex items-start gap-3 rounded-2xl border border-[var(--tt-line)] bg-white p-4">
              <CheckCircle2 className="h-5 w-5 text-[var(--tt-green)] mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--tt-ink)]">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile form + FAQ */}
      <section id="quote" className="mx-auto max-w-7xl px-6 pb-14 lg:hidden">
        <FreightQuoteForm
          defaultMode={cfg.defaults.mode}
          defaultOrigin={cfg.defaults.origin}
          defaultDestination={cfg.defaults.destination}
          defaultOriginPort={cfg.defaults.originPort}
          defaultDestinationPort={cfg.defaults.destinationPort}
        />
      </section>

      <section className="mx-auto max-w-4xl px-6 py-14">
        <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">FAQ</div>
        <h2 className="font-display font-black text-[var(--tt-navy)] text-3xl md:text-4xl tracking-tight mt-1 mb-8">
          Common questions
        </h2>
        <div className="space-y-3" data-testid="freight-route-faqs">
          {cfg.faqs.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-[var(--tt-line)] bg-white p-5 open:shadow-sm transition">
              <summary className="cursor-pointer list-none flex items-center justify-between font-semibold text-[var(--tt-navy)]">
                <span>{f.q}</span>
                <ChevronRight className="h-4 w-4 text-[var(--tt-green)] group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-sm text-[var(--tt-ink-soft)] mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Other lanes */}
      <section className="mx-auto max-w-7xl px-6 pb-16">
        <div className="rounded-3xl border border-[var(--tt-line)] bg-white p-6 md:p-8">
          <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">Other lanes</div>
          <h3 className="font-display font-black text-[var(--tt-navy)] text-2xl md:text-3xl tracking-tight mt-1 mb-5">
            Explore other freight routes
          </h3>
          <div className="grid md:grid-cols-3 gap-3">
            {Object.values(FREIGHT_ROUTES).filter((r) => r.slug !== slug).map((r) => (
              <Link key={r.slug} to={`/freight/${r.slug}`} data-testid={`freight-other-${r.slug}`} className="group rounded-2xl border border-[var(--tt-line)] bg-[var(--tt-cream)] p-5 hover:border-[var(--tt-green)] hover:bg-white hover:-translate-y-0.5 transition-all">
                <div className="text-[10px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">{r.hero.eyebrow}</div>
                <div className="font-semibold text-[var(--tt-navy)] mt-1 leading-snug">{r.hero.title.slice(0, 60)}…</div>
                <div className="text-xs text-[var(--tt-green)] font-semibold inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
                  View lane <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreightRoutePage;
